import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Schedule as ProcessingIcon,
  FiberNew as NewIcon,
  Cancel as CancelledIcon,
  Block as BlockedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface RxStatusBadgeProps {
  status?: number;
  statusDescription?: string;
  statusCategory?: string;
  canFill?: boolean;
  inactive?: boolean;
  isMistake?: boolean;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  showFillable?: boolean;
}

export const RxStatusBadge: React.FC<RxStatusBadgeProps> = ({
  status,
  statusDescription,
  statusCategory,
  canFill,
  inactive,
  isMistake,
  size = 'small',
  showIcon = true,
  showFillable = false,
}) => {
  // Handle error states first
  if (isMistake) {
    return (
      <Tooltip title="This prescription was marked as a data entry error and should not be filled">
        <Chip
          label="Data Entry Error"
          color="error"
          size={size}
          icon={showIcon ? <ErrorIcon /> : undefined}
        />
      </Tooltip>
    );
  }

  if (inactive) {
    return (
      <Tooltip title="This prescription is marked as inactive">
        <Chip
          label="Inactive"
          color="default"
          size={size}
          icon={showIcon ? <BlockedIcon /> : undefined}
        />
      </Tooltip>
    );
  }

  // Determine color and icon based on status
  const getStatusConfig = () => {
    switch (status) {
      case 1: // Active - Ready to Fill
      case 6: // Active - On File
        return {
          color: 'success' as const,
          icon: <ActiveIcon />,
          label: statusDescription || 'Active',
        };

      case 3: // Unfilled/New
        return {
          color: 'info' as const,
          icon: <NewIcon />,
          label: statusDescription || 'Unfilled',
        };

      case 2: // In Process
      case 7: // In Process - Hold
        return {
          color: 'warning' as const,
          icon: <ProcessingIcon />,
          label: statusDescription || 'In Process',
        };

      case 8: // Active - Suspended
        return {
          color: 'warning' as const,
          icon: <WarningIcon />,
          label: statusDescription || 'Suspended',
        };

      case 4: // Cancelled
      case 5: // Deleted/Void
        return {
          color: 'error' as const,
          icon: <CancelledIcon />,
          label: statusDescription || 'Cancelled',
        };

      default:
        return {
          color: 'default' as const,
          icon: undefined,
          label: statusDescription || 'Unknown',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box display="flex" gap={1} alignItems="center">
      <Tooltip title={`Status Code: ${status || 'N/A'} - ${statusCategory || 'Unknown'}`}>
        <Chip
          label={config.label}
          color={config.color}
          size={size}
          icon={showIcon ? config.icon : undefined}
        />
      </Tooltip>

      {showFillable && canFill !== undefined && (
        <Chip
          label={canFill ? 'Fillable' : 'Not Fillable'}
          color={canFill ? 'success' : 'default'}
          size={size}
          variant="outlined"
        />
      )}
    </Box>
  );
};

// Helper component for displaying status in a compact way
export const RxStatusIndicator: React.FC<{
  status?: number;
  canFill?: boolean;
}> = ({ status, canFill }) => {
  const getColor = () => {
    if (!status) return 'text.disabled';

    switch (status) {
      case 1:
      case 6:
        return 'success.main';
      case 3:
        return 'info.main';
      case 2:
      case 7:
      case 8:
        return 'warning.main';
      case 4:
      case 5:
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getStatusText = () => {
    if (!status) return '—';

    switch (status) {
      case 1: return 'Active';
      case 2: return 'Processing';
      case 3: return 'New';
      case 4: return 'Cancelled';
      case 5: return 'Void';
      case 6: return 'On File';
      case 7: return 'Hold';
      case 8: return 'Suspended';
      default: return 'Unknown';
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: getColor(),
        }}
      />
      <Typography variant="caption" color={getColor()}>
        {getStatusText()}
      </Typography>
      {canFill !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {canFill ? ' • Fillable' : ''}
        </Typography>
      )}
    </Box>
  );
};